import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Coins, Sparkles, Crown, Search, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { UserAvatar } from '../components/RankBadge';
import GiftFly from '../components/GiftFly';
import { COSMIC_RANKS } from '../mock/mock';
import { toast } from 'sonner';

const RARITY_COLOR = {
  common: { ring: 'rgba(168,168,179,0.5)', text: '#A8A8B3' },
  rare: { ring: 'rgba(0,198,255,0.6)', text: '#00C6FF' },
  epic: { ring: 'rgba(244,114,182,0.6)', text: '#F472B6' },
  legendary: { ring: 'rgba(251,191,36,0.7)', text: '#FBBF24' },
  mythic: { ring: 'rgba(123,47,247,0.8)', text: '#C084FC' },
};

const CATEGORIES = [
  { id: 'all', label: 'All', emoji: '✨' },
  { id: 'everyday', label: 'Everyday', emoji: '🌹', desc: 'Sweet little gestures for any moment.' },
  { id: 'space', label: 'Space', emoji: '🚀', desc: 'Sci-fi flair across the cosmos.' },
  { id: 'exclusive', label: 'WorldVibe', emoji: '🪐', desc: 'Exclusive to our universe.' },
  { id: 'luxury', label: 'Luxury', emoji: '💎', desc: 'High-end statements of love.' },
  { id: 'relationship', label: 'Relationship', emoji: '💍', desc: 'For dating, engagement and marriage.' },
];

export default function Gifts() {
  const { user, refresh, openBuyStars, creditStars } = useAuth();
  const [gifts, setGifts] = useState([]);
  const [online, setOnline] = useState([]);
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [fly, setFly] = useState(null);

  useEffect(() => {
    api.get('/gifts').then(r => setGifts(r.data)).catch(() => {});
    api.get('/users/online').then(r => setOnline(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return gifts
      .filter(g => cat === 'all' ? true : g.category === cat)
      .filter(g => !q.trim() || g.name.toLowerCase().includes(q.trim().toLowerCase()));
  }, [gifts, cat, q]);

  const confirmSend = async () => {
    if (!selected || !recipient) return;
    // Pre-check: open BuyStars if user can't afford
    if ((user?.stars ?? 0) < selected.price) {
      const need = selected.price - (user?.stars ?? 0);
      toast.error(`Need ${need.toLocaleString()} more ⭐ for ${selected.name}. Top up to send it!`);
      setSelected(null); setRecipient(null);
      openBuyStars();
      return;
    }
    const giftSnap = { emoji: selected.emoji, name: `${selected.name} → ${recipient.displayName}` };
    try {
      await api.post('/gifts/send', { gift_id: selected.id, recipient_id: recipient.user_id });
      creditStars(-selected.price); // optimistic debit
      setFly(giftSnap);
      toast.success(`Sent ${selected.name} ${selected.emoji}`);
      setSelected(null); setRecipient(null);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /not enough stars/i.test(detail)) {
        setSelected(null); setRecipient(null);
        toast.error('Not enough stars — open Buy Stars to top up');
        openBuyStars();
        return;
      }
      toast.error(detail || 'Could not send gift');
    }
  };

  const currentCat = CATEGORIES.find(c => c.id === cat);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl md:text-3xl">Cosmic Gift Shop</h1>
          <p className="text-white/60 mt-1 text-sm">50 unique gifts across 5 categories.</p>
        </div>
        <div className="flex items-center gap-3 glass rounded-full px-4 py-2">
          <span className="flex items-center gap-1 text-amber-300 text-sm font-semibold"><Star size={14} fill="currentColor" />{user?.stars?.toLocaleString() || 0}</span>
          <span className="w-px h-4 bg-white/10" />
          <span className="flex items-center gap-1 text-cyan-300 text-sm font-semibold"><Coins size={14} />{user?.credits?.toLocaleString() || 0}</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search gifts..." className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
      </div>

      {/* Category tabs */}
      <Tabs value={cat} onValueChange={setCat}>
        <TabsList className="bg-[#161622] border border-white/10 flex-wrap h-auto">
          {CATEGORIES.map(c => (
            <TabsTrigger key={c.id} value={c.id} className="data-[state=active]:bg-white/10 flex items-center gap-1.5">
              <span>{c.emoji}</span>{c.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={cat} className="mt-5">
          {currentCat?.desc && cat !== 'all' && (
            <div className="text-sm text-white/60 mb-4 flex items-center gap-2">
              <span className="text-2xl">{currentCat.emoji}</span>
              <span>{currentCat.desc}</span>
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filtered.map(g => {
              const c = RARITY_COLOR[g.rarity] || RARITY_COLOR.common;
              return (
                <Dialog key={g.id}>
                  <DialogTrigger asChild>
                    <button onClick={() => { setSelected(g); setRecipient(null); }} className="group rounded-2xl p-4 bg-[#161622] border hover:-translate-y-1 hover:shadow-xl" style={{ borderColor: c.ring + '55' }}>
                      <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">{g.emoji}</div>
                      <div className="font-semibold text-[11px] truncate">{g.name}</div>
                      <div className="text-[9px] uppercase tracking-wider mt-1" style={{ color: c.text }}>{g.rarity}</div>
                      <div className="text-[11px] mt-1 flex items-center justify-center gap-1 text-amber-300"><Star size={9} fill="currentColor" />{g.price.toLocaleString()}</div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md">
                    <DialogHeader><DialogTitle className="font-display">Send {selected?.name} {selected?.emoji}</DialogTitle></DialogHeader>
                    <div className="text-center py-3">
                      <div className="text-7xl mb-2">{selected?.emoji}</div>
                      <div className="text-amber-300 flex items-center justify-center gap-1"><Star size={14} fill="currentColor" />{selected?.price.toLocaleString()} stars</div>
                      <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{selected?.rarity} · {selected?.category}</div>
                    </div>
                    <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Recipient</div>
                    <div className="max-h-60 overflow-y-auto scroll-hide space-y-1">
                      {online.map(u => (
                        <button key={u.user_id} onClick={() => setRecipient(u)} className={`w-full flex items-center gap-3 p-2 rounded-lg ${recipient?.user_id === u.user_id ? 'bg-fuchsia-500/20 border border-fuchsia-500/40' : 'hover:bg-white/5'}`}>
                          <UserAvatar user={u} size={32} ring={false} />
                          <span className="text-sm flex-1 text-left">{u.displayName}</span>
                        </button>
                      ))}
                    </div>
                    <Button onClick={confirmSend} disabled={!recipient} className="w-full mt-3 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0">
                      <Sparkles size={14} className="mr-2" /> Send Gift
                    </Button>
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>

          {filtered.length === 0 && <div className="text-center py-12 text-white/40">No gifts match your search.</div>}
        </TabsContent>
      </Tabs>

      <RankUpgradeCard user={user} />
      {fly && <GiftFly emoji={fly.emoji} name={fly.name} onDone={() => setFly(null)} />}
    </div>
  );
}

function RankUpgradeCard({ user }) {
  const nav = useNavigate();
  const currentRank = user?.rank || 1;
  const xp = user?.xp || 0;
  const cur = COSMIC_RANKS.find(r => r.rank === currentRank) || COSMIC_RANKS[0];
  const next = COSMIC_RANKS.find(r => r.rank === currentRank + 1) || cur;
  const atMax = next.rank === cur.rank;
  const span = Math.max(1, next.xp - cur.xp);
  const progress = atMax ? 100 : Math.min(100, Math.max(0, ((xp - cur.xp) / span) * 100));
  const remaining = Math.max(0, next.xp - xp);

  return (
    <Card
      className="border"
      style={{ background: `linear-gradient(135deg, ${next.color}1A, ${cur.color}10)`, borderColor: `${next.color}33` }}
      data-testid="rank-upgrade-card"
    >
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: `${next.color}22`, border: `1px solid ${next.color}55` }}
          >
            <Crown size={22} style={{ color: next.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-wider text-white/40">Next rank</div>
              <div className="font-display font-bold text-base sm:text-lg" style={{ color: next.color }}>
                {atMax ? `You're at the top — ${cur.name}` : `Become a ${next.name}`}
              </div>
            </div>
            {!atMax && (
              <div className="text-xs text-white/60 mt-0.5 line-clamp-2">
                {(next.benefits?.[0] || 'Unlock exclusive perks')}{next.benefits?.[1] ? ` · ${next.benefits[1]}` : ''}
              </div>
            )}
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px] text-white/60">
                <span className="flex items-center gap-1" data-testid="rank-progress-xp">
                  <span className="text-cyan-300">⚡</span>
                  {xp.toLocaleString()} <span className="text-white/30">XP / {atMax ? '∞' : next.xp.toLocaleString()}</span>
                </span>
                {!atMax && (
                  <span data-testid="rank-progress-remaining">
                    <span className="font-semibold text-white">{remaining.toLocaleString()}</span> XP to go
                  </span>
                )}
              </div>
              <div className="mt-1.5 h-1.5 bg-white/8 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${cur.color}, ${next.color})` }}
                />
              </div>
              <div className="text-[10px] text-white/40 mt-1.5">
                XP is earned by chatting, playing games, and daily logins — not purchased.
              </div>
            </div>
          </div>
          <Button
            onClick={() => nav('/app')}
            disabled={atMax}
            data-testid="rank-upgrade-btn"
            className="sm:self-center bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white hover:from-cyan-500 hover:to-fuchsia-600 rounded-full font-semibold border-0 disabled:opacity-50 disabled:from-white/20 disabled:to-white/20"
          >
            {atMax ? 'Max rank' : <>View tasks to earn XP <ArrowRight size={14} className="ml-1" /></>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
